// AI chat wire contract (ADR-0050). The gerencial dashboard's IA Insights
// chat: a facility_management manager asks natural-language questions
// about their portfolio and gets answers grounded in the same org-scoped
// data the dashboard already renders.
//
// Transport (ADR-0050): dashboard → core-api POST /ia/chat (authed,
// org-scoped, assembles the grounding facts server-side) → ai-svc
// POST /internal/chat (private network, X-Internal-Token) → AiProvider.
// Only the client-facing request/response live here. The internal
// core-api → ai-svc payload is backend-private and defined in ai-svc's
// internal-chat route.
//
// There is no chat engine entity to derive from, so per §0.4(7) this
// wire schema is the source of truth by necessity (same posture as the
// queue payloads). If a conversation entity ever lands in core-engine,
// re-derive via omit/pick.
import { z } from "zod";
import { STORE_COMPARISON_COLUMNS_MAX, STORE_COMPARISON_ROWS_MAX, storeComparisonColumnKey, storeComparisonSortDir, storeComparisonTable, } from "./store-comparison.js";
// Conversation history carried per turn. The chat is stateless on the
// server (ADR-0050: ephemeral / client-only transcripts) — the dashboard
// round-trips the prior turns so the model has context. Capped so the
// uncached `messages` payload can't grow without bound.
export const IA_CHAT_HISTORY_MAX = 12;
// ADR-0167 — the answer when a turn would otherwise carry no text: the
// model called the table tool with nothing to say and the request was
// dropped (or its only text was a stripped pipe table). An empty answer
// must never reach the client — it would round-trip as an empty history
// turn, which iaChatTurn rejects, and break every later turn.
export const IA_CHAT_TABLE_FALLBACK_ANSWER = "No pude armar esa tabla con los datos disponibles.";
export const iaChatTurn = z.object({
    role: z.enum(["user", "assistant"]),
    content: z.string().min(1).max(8000),
});
export const iaChatRequest = z
    .object({
    question: z.string().trim().min(1).max(2000),
    history: z.array(iaChatTurn).max(IA_CHAT_HISTORY_MAX).default([]),
})
    .strict();
// Entity reference handle: a short per-turn alias core-api prints in the
// facts block ({T3} store, {R3} that store's latest report, {O12} an open
// observation). The model never sees a UUID — it can only cite a handle,
// and core-api resolves it against the scoped rows of the same turn.
export const IA_CHAT_HANDLE_RE = /^[TRO]\d{1,4}$/;
export const IA_CHAT_ENTITIES_MAX = 200;
export const iaChatEntityKind = z.enum(["store", "report", "finding"]);
export const iaChatEntity = z
    .object({
    handle: z.string().regex(IA_CHAT_HANDLE_RE),
    kind: iaChatEntityKind,
    id: z.string().uuid(),
    // Server-assigned canonical name (store name, report date, finding
    // category · area) — never text the model chose, so an injected
    // observation cannot dress a link up as a different entity.
    label: z.string().min(1).max(300),
})
    .strict();
export const iaChatResponse = z
    .object({
    // Free-form prose; may contain a constrained markdown subset — bold
    // and simple lists only, per the "Formato:" rule in
    // apps/ai-svc/src/prompts/chat.ts — plus entity tokens `[[T3]]`.
    // The format rule is a prompt-enforced, therefore probabilistic,
    // instruction (no schema can validate it);
    // apps/ai-svc/src/providers/anthropic.ts's normalizeChatAnswer is
    // the deterministic backstop that strips anything the format rule
    // failed to prevent (headings, links, images) before this reaches
    // core-api, and core-api rewrites every entity token: a handle it
    // resolved stays as `[[T3]]` with a matching `entities` row, anything
    // else becomes plain text. The dashboard renders `answer` through an
    // allowlist markdown-to-React renderer
    // (apps/web-dashboard/src/lib/markdown.ts) — never assume literal
    // plain text, and never render it as raw HTML. Capped to match
    // iaChatTurn.content's bound, since every assistant answer
    // round-trips back as history on the next turn.
    answer: z.string().max(8000),
    // false when the model determined the portfolio facts don't support
    // an answer (the [SIN_DATO] sentinel). Lets the dashboard render a
    // "no tengo ese dato" affordance distinct from a real answer.
    grounded: z.boolean(),
    // One row per `[[handle]]` left in `answer`. The client builds the
    // route from `kind` + `id` against a fixed table; the server never
    // sends a URL. Absent on answers without links (and on the
    // ai-svc → core-api hop, which never carries it).
    entities: z.array(iaChatEntity).max(IA_CHAT_ENTITIES_MAX).optional(),
    // ADR-0167 — present when the model asked for a table with the chat
    // tool. Every cell comes from core-api's store-comparison builder,
    // never from the model; `answer` carries only the framing sentence.
    // Deploy order (strict parsers on both hops): api-types + dashboard
    // first, then core-api, then ai-svc.
    table: storeComparisonTable.optional(),
})
    .strict();
// ADR-0167 — the table the model asked for, as ai-svc forwards it on the
// ai-svc → core-api hop (never to the client). Rows are per-turn store
// handles ({T3}) or "all" — the full scope, not only the stores the
// facts budget showed. core-api resolves each handle only against the
// store handles this turn minted (the same entity map that resolves
// `[[T3]]` links: a store line the model actually read), so a
// budget-cut, hallucinated or out-of-scope handle matches nothing.
export const IA_CHAT_TABLE_ROW_HANDLE_RE = /^T\d{1,4}$/;
export const iaChatTableRequest = z
    .object({
    rows: z.union([
        z.literal("all"),
        z.array(z.string().regex(IA_CHAT_TABLE_ROW_HANDLE_RE)).min(1).max(STORE_COMPARISON_ROWS_MAX),
    ]),
    columns: z.array(storeComparisonColumnKey).min(1).max(STORE_COMPARISON_COLUMNS_MAX),
    sortBy: storeComparisonColumnKey.optional(),
    sortDir: storeComparisonSortDir.optional(),
    limit: z.number().int().min(1).max(STORE_COMPARISON_ROWS_MAX).optional(),
})
    .strict();
//# sourceMappingURL=ai.js.map